import { useEffect, useMemo, useState } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { Asset, useListAssetsQuery } from "features/trading/accounts";
import { SelectedAsset } from "helpers/types";
import { useDebounce } from "use-debounce";

const MIN_SEARCH_LENGTH = 2;
const ASSET_PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 250;
const DEFAULT_PLACEHOLDER = "Search by symbol or name...";
const DEFAULT_LABEL = "Asset";

export const getAssetLabel = (asset: Pick<Asset, "symbol" | "name">) =>
  `${asset.symbol} - ${asset.name}`;

const toSelectedAsset = ({ id, symbol, name }: Asset): SelectedAsset => ({
  id,
  symbol,
  name,
});

const getNoOptionsText = (trimmedSearch: string) =>
  trimmedSearch.length < MIN_SEARCH_LENGTH
    ? "Type at least 2 characters to search"
    : "No assets found";

const useAssetSearch = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);
  const trimmedSearch = debouncedSearchTerm.trim();

  const {
    data: assetsData,
    isLoading,
    isFetching,
  } = useListAssetsQuery(
    { search: trimmedSearch, page_size: ASSET_PAGE_SIZE },
    { skip: trimmedSearch.length < MIN_SEARCH_LENGTH },
  );

  return {
    searchTerm,
    setSearchTerm,
    trimmedSearch,
    results: assetsData?.results ?? [],
    loading: isLoading || isFetching,
  };
};

interface BaseAssetAutocompleteProps {
  label?: string;
  placeholder?: string;
}

interface SingleAssetAutocompleteProps extends BaseAssetAutocompleteProps {
  multiple?: false;
  value: number | "";
  onChange: (assetId: number | "") => void;
  required?: boolean;
}

interface MultiAssetAutocompleteProps extends BaseAssetAutocompleteProps {
  multiple: true;
  value: SelectedAsset[];
  onChange: (assets: SelectedAsset[]) => void;
}

type AssetAutocompleteProps =
  | SingleAssetAutocompleteProps
  | MultiAssetAutocompleteProps;

const LoadingAdornment = ({ loading }: { loading: boolean }) =>
  loading ? <CircularProgress color="inherit" size={20} /> : null;

function SingleAssetAutocomplete({
  value,
  onChange,
  label = DEFAULT_LABEL,
  placeholder = DEFAULT_PLACEHOLDER,
  required = false,
}: SingleAssetAutocompleteProps) {
  const { searchTerm, setSearchTerm, trimmedSearch, results, loading } =
    useAssetSearch();
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

  const options = useMemo(() => {
    if (selectedAsset && !results.some((a) => a.id === selectedAsset.id)) {
      return [selectedAsset, ...results];
    }
    return results;
  }, [results, selectedAsset]);

  useEffect(() => {
    if (value === "") {
      setSelectedAsset(null);
      setSearchTerm("");
    }
  }, [value, setSearchTerm]);

  const clearSelection = () => {
    setSelectedAsset(null);
    setSearchTerm("");
    onChange("");
  };

  return (
    <Autocomplete
      options={options}
      filterOptions={(items) => items}
      getOptionLabel={getAssetLabel}
      isOptionEqualToValue={(option, item) => option.id === item.id}
      value={selectedAsset}
      loading={loading}
      onInputChange={(_, newValue, reason) => {
        if (reason === "input") {
          setSearchTerm(newValue);
          if (selectedAsset) clearSelection();
        } else if (reason === "clear") {
          clearSelection();
        }
      }}
      onChange={(_, asset) => {
        setSelectedAsset(asset);
        onChange(asset?.id ?? "");
        if (asset) setSearchTerm(getAssetLabel(asset));
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                <LoadingAdornment loading={loading} />
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={getNoOptionsText(trimmedSearch)}
    />
  );
}

function MultiAssetAutocomplete({
  value,
  onChange,
  label = DEFAULT_LABEL,
  placeholder = DEFAULT_PLACEHOLDER,
}: MultiAssetAutocompleteProps) {
  const { searchTerm, setSearchTerm, trimmedSearch, results, loading } =
    useAssetSearch();

  const options = useMemo<SelectedAsset[]>(() => {
    const resultsAsSelected: SelectedAsset[] = results.map(toSelectedAsset);
    const resultIds = new Set(resultsAsSelected.map((r) => r.id));
    const missing = value.filter((v) => !resultIds.has(v.id));
    return [...missing, ...resultsAsSelected];
  }, [results, value]);

  const normalize = (assets: SelectedAsset[]): SelectedAsset[] =>
    assets.map(({ id, symbol, name }) => ({ id, symbol, name }));

  return (
    <Autocomplete<SelectedAsset, true>
      multiple
      options={options}
      filterOptions={(items) => items}
      getOptionLabel={getAssetLabel}
      isOptionEqualToValue={(option, item) => option.id === item.id}
      value={value}
      inputValue={searchTerm}
      loading={loading}
      onInputChange={(_, next, reason) => {
        if (reason === "input") setSearchTerm(next);
        else if (reason === "clear" || reason === "reset") setSearchTerm("");
      }}
      onChange={(_, assets) => {
        onChange(normalize(assets));
        setSearchTerm("");
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? placeholder : ""}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                <LoadingAdornment loading={loading} />
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={getNoOptionsText(trimmedSearch)}
    />
  );
}

export default function AssetAutocomplete(props: AssetAutocompleteProps) {
  return props.multiple ? (
    <MultiAssetAutocomplete {...props} />
  ) : (
    <SingleAssetAutocomplete {...props} />
  );
}
